import { Brain, ChevronDown, ChevronUp, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useEffect, useState, useRef, useCallback } from "react";
import { useLocale } from "@/hooks/useLocale";

interface ThoughtEntry {
  text: string;
  timestamp: number;
}

interface CharacterThoughtsProps {
  thought: string | null;
  isGenerating?: boolean;
  characterName?: string;
  adventureId?: string;
}

const LOG_KEY = (id: string) => `thought-log-${id}`;

function loadLog(adventureId?: string): ThoughtEntry[] {
  if (!adventureId) return [];
  try {
    const raw = localStorage.getItem(LOG_KEY(adventureId));
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveLog(adventureId: string | undefined, log: ThoughtEntry[]) {
  if (!adventureId) return;
  try { localStorage.setItem(LOG_KEY(adventureId), JSON.stringify(log.slice(0, 10))); } catch {}
}

export function CharacterThoughts({ thought, isGenerating, characterName, adventureId }: CharacterThoughtsProps) {
  const { t } = useLocale();
  const [visible, setVisible] = useState(false);
  const [displayedThought, setDisplayedThought] = useState<string | null>(() => {
    // Restore last displayed thought from log on mount
    const log = loadLog(adventureId);
    return log.length > 0 ? log[0].text : null;
  });
  const [thoughtLog, setThoughtLog] = useState<ThoughtEntry[]>(() => loadLog(adventureId));
  const [showLog, setShowLog] = useState(false);
  const prevThoughtRef = useRef<string | null>(null);

  // On mount, mark visible if we have a restored thought
  useEffect(() => {
    if (displayedThought) setVisible(true);
  }, []);

  useEffect(() => {
    if (thought && thought !== prevThoughtRef.current) {
      prevThoughtRef.current = thought;

      // Add to history log
      setThoughtLog(prev => {
        const updated = [{ text: thought, timestamp: Date.now() }, ...prev].slice(0, 10);
        saveLog(adventureId, updated);
        return updated;
      });

      // Animate in
      setVisible(false);
      const timer = setTimeout(() => {
        setDisplayedThought(thought);
        setVisible(true);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [thought, adventureId]);

  // Never show placeholder if we have any thought (current or previously displayed)
  const showPlaceholder = !thought && !displayedThought && !isGenerating;
  const placeholderText = characterName
    ? `${characterName} ${t.play_observing}`
    : t.play_awaiting_thoughts;

  const timeAgo = (ts: number) => {
    const s = Math.floor((Date.now() - ts) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    return `${Math.floor(s / 3600)}h ago`;
  };

  // Re-render timestamps
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!showLog || thoughtLog.length === 0) return;
    const iv = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(iv);
  }, [showLog, thoughtLog.length]);

  const hasLog = thoughtLog.length > 1;

  return (
    <div className="mt-3">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Brain className="w-3.5 h-3.5 text-accent/60" />
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex-1">
          {characterName ? `${characterName}` : t.play_inner_voice}
        </span>
        {hasLog && (
          <button
            onClick={() => setShowLog(!showLog)}
            className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
          >
            <Clock className="w-3 h-3" />
            <span>{t.play_thoughts_log} ({thoughtLog.length})</span>
            {showLog ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        )}
      </div>

      {/* Current thought */}
      <div
        className={cn(
          "relative rounded-lg border border-accent/30 bg-accent/5 px-3 py-2.5 transition-all duration-300",
          showPlaceholder ? "opacity-100" : (visible || displayedThought) ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1",
        )}
      >
        <span className="absolute -top-1.5 left-2.5 text-accent/40 text-lg font-serif leading-none select-none">
          "
        </span>

        {isGenerating && !displayedThought ? (
          <div className="space-y-1.5 py-0.5">
            <Skeleton className="h-3 w-[85%] bg-accent/10" />
            <Skeleton className="h-3 w-[60%] bg-accent/10" />
          </div>
        ) : showPlaceholder ? (
          <p className="text-xs italic text-muted-foreground/50 leading-relaxed pl-1">
            {placeholderText}
          </p>
        ) : (
          <p className="text-xs italic text-foreground/80 leading-relaxed pl-1">
            {displayedThought}
          </p>
        )}

        <span className="absolute -bottom-1.5 right-2.5 text-accent/40 text-lg font-serif leading-none select-none">
          "
        </span>
      </div>

      {/* Thought log */}
      {showLog && hasLog && (
        <div className="mt-2 rounded-lg border border-border/50 bg-muted/20 overflow-hidden">
          <div className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider bg-muted/40 border-b border-border/30">
            {t.play_recent_thoughts}
          </div>
          <div className="max-h-48 overflow-y-auto divide-y divide-border/20">
            {thoughtLog.map((entry, i) => (
              <div
                key={`${entry.timestamp}-${i}`}
                className={cn(
                  "px-3 py-2 text-xs transition-colors",
                  i === 0 ? "bg-accent/5 text-foreground/90" : "text-foreground/60 hover:bg-accent/5",
                )}
              >
                <div className="flex items-start gap-2">
                  <span className="text-accent/40 font-serif leading-none mt-0.5 shrink-0">"</span>
                  <p className="italic leading-relaxed flex-1 min-w-0">{entry.text}</p>
                  <span className="text-[9px] text-muted-foreground shrink-0 mt-0.5">
                    {timeAgo(entry.timestamp)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}